import React from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import {
  growthNotice,
  growthStep,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { formatDayMonth } from "@/lib/dates";
import { spentLimits, type MeteredLimit } from "@/lib/api/organizations";
import { OrgBanner } from "./OrgBanner";

const MONTHLY: Record<Exclude<MeteredLimit, "storage">, string> = {
  egress: "data transfer",
  mcp_calls: "MCP tool calls",
};

/// What is spent, then the step the reader can take. The monthly allowances
/// share a reset day; storage carries none, because waiting never frees it.
export function spentLine(
  spent: MeteredLimit[],
  resetsOn: string,
  isOwner: boolean,
): string {
  const monthly = spent.flatMap((l) => (l === "storage" ? [] : [MONTHLY[l]]));
  const plural = monthly.length > 1 || monthly[0] === MONTHLY.mcp_calls;
  const used = `this month's ${monthly.join(" and ")}`;
  if (!spent.includes("storage")) {
    const more = isOwner
      ? "For more now, move to a larger plan."
      : "For more now, an owner can move it to a larger plan.";
    return `This organization has used up ${used}. ${plural ? "They come" : "It comes"} back on ${resetsOn}. ${more}`;
  }
  return monthly.length === 0
    ? growthNotice("out_of_storage", isOwner)
    : `This organization is out of storage and has used up ${used}, which ${plural ? "come" : "comes"} back on ${resetsOn}. ${growthStep("out_of_storage", isOwner)}`;
}

/// What stops the whole org growing, for every member (D59): one who cannot fix
/// it still needs to know why their write failed, and is told who can act.
/// The grace week warns its owners alone: nothing is refused yet, and only an
/// owner can replace the card. The 80% warning is the rail's.
export const PlanBanner: React.FC = () => {
  const navigate = useNavigate();
  const { org, usage } = useOrgContextStore(
    useShallow((s) => ({
      org: s.orgs.find((o) => o.org_id === s.activeOrgId),
      usage: s.usage,
    })),
  );

  // The deletion banner sits directly above and is louder.
  if (!usage || !org || org.deletion_scheduled_at) return null;

  // Buying a plan and replacing the card are `can_manage_billing`, an owner's
  // alone: an admin can read Plan & usage but would find both disabled there.
  const isOwner = org.role === "owner";
  const toPlan = (label: string, state?: { updateCard: true }) =>
    isOwner
      ? {
          label,
          onClick: () =>
            navigate(`/organizations/${org.org_id}#plan`, { state }),
        }
      : undefined;

  // Past due outranks a spent limit, as at the gate: paying is what lifts it.
  if (usage.standing.state === "read_only") {
    return (
      <OrgBanner action={toPlan("Update payment method", { updateCard: true })}>
        Payment is overdue, so this organization is{" "}
        <span className="font-medium">read-only</span> until it&apos;s paid. If
        it isn&apos;t, the plan ends on{" "}
        {formatDayMonth(usage.standing.revoke_on * 1000)}.
        {!isOwner && " An owner can update the payment method."}
      </OrgBanner>
    );
  }

  const spent = spentLimits(usage);
  if (spent.length > 0) {
    return (
      <OrgBanner action={toPlan("Plan & usage")}>
        {spentLine(
          spent,
          formatDayMonth(usage.egress.resets_at * 1000),
          isOwner,
        )}
      </OrgBanner>
    );
  }

  if (usage.standing.state === "grace" && isOwner) {
    return (
      <OrgBanner
        tone="warning"
        action={toPlan("Update payment method", { updateCard: true })}
      >
        Payment failed. Update the payment method before{" "}
        {formatDayMonth(usage.standing.read_only_on * 1000)} to keep adding data
        and members.
      </OrgBanner>
    );
  }
  return null;
};
